import * as React from "react";
import DateFns from "date-fns";
import MiniContainer from "../components/container/MiniContainer";
import Spinner from "../components/loading/Spinner";
import { parseAPIDate } from "../lib/dateUtils/dateUtils";
import { formRepliesGET, formReplyPreviewPOST, formReplyPATCH, formReplyDELETE } from "./api";
import "./FormReplies.css";

// Website form replies (Ben and Natalie, 4 Oct 2026): what each form on
// skimpyheaven.com.au emails back to the person who filled it in. Each form
// starts on the built-in reply it always sent; saving here changes that form's
// reply, Reset to built-in goes back. The subject line and who it goes to are
// fixed in code. Admin only, enforced on the API (api/formreplies).

const FIELDS = ["preview", "heading", "body", "call_button", "centred", "closing"];

function pick(t) {
	const out = {};
	FIELDS.forEach(k => {
		out[k] = t ? t[k] : undefined;
	});
	out.preview = out.preview || "";
	out.heading = out.heading || "";
	out.body = out.body || "";
	out.closing = out.closing || "";
	out.call_button = !!out.call_button;
	out.centred = !!out.centred;
	return out;
}

function same(a, b) {
	return FIELDS.every(k => a[k] === b[k]);
}

function errorText(e, fallback) {
	return (e && e.response && e.response.data && e.response.data.error) || fallback;
}

class FormReplies extends React.Component {
	state = {
		forms: null,
		key: null,
		draft: null,
		previewHTML: "",
		previewing: false,
		busy: false,
		confirmReset: false,
		error: null,
		notice: null
	};

	componentDidMount() {
		formRepliesGET()
			.then(r => {
				const forms = (r.data && r.data.forms) || [];
				this.setState({ forms }, () => forms.length && this.select(forms[0].key));
			})
			.catch(e => this.setState({ error: errorText(e, "Could not load the form replies.") }));
	}

	componentWillUnmount() {
		clearTimeout(this.previewTimer);
	}

	form(key = this.state.key) {
		return (this.state.forms || []).find(f => f.key === key) || null;
	}

	// What the form sends now: its saved reply, or the built-in one.
	current(f) {
		return pick(f.saved || f.built_in);
	}

	select(key) {
		if (key !== this.state.key && this.state.draft && this.dirty()) {
			// Unsaved edits on this form: keep them until she saves or discards.
			this.setState({ notice: null, error: "Save or discard the changes to this reply before opening another." });
			return;
		}
		const f = this.form(key);
		if (!f) return;
		this.setState({ key, draft: this.current(f), confirmReset: false, error: null, notice: null }, () => this.showSending(f));
	}

	dirty() {
		const f = this.form();
		return !!(f && this.state.draft && !same(this.state.draft, this.current(f)));
	}

	// The preview of what the form sends today: the built-in email itself while
	// nothing is saved, otherwise the saved reply rendered by the server.
	showSending(f) {
		if (!f.saved) {
			this.setState({ previewHTML: f.built_in_html });
			return;
		}
		this.refreshPreview();
	}

	set(k, v) {
		this.setState({ draft: { ...this.state.draft, [k]: v }, notice: null, error: null, confirmReset: false }, () => {
			clearTimeout(this.previewTimer);
			this.previewTimer = setTimeout(() => this.refreshPreview(), 500);
		});
	}

	refreshPreview() {
		const { key, draft } = this.state;
		if (!key || !draft) return;
		this.setState({ previewing: true });
		formReplyPreviewPOST(key, draft)
			.then(r => this.setState({ previewHTML: r.data.html, previewing: false }))
			.catch(e => this.setState({ previewing: false, error: errorText(e, "The preview could not be made.") }));
	}

	replaceForm(updated) {
		this.setState({ forms: this.state.forms.map(f => (f.key === updated.key ? updated : f)) });
	}

	save() {
		const f = this.form();
		this.setState({ busy: true, error: null, notice: null });
		formReplyPATCH(f.key, this.state.draft)
			.then(r => {
				this.replaceForm(r.data);
				this.setState({ busy: false, draft: this.current(r.data), notice: "Saved. Every " + f.label + " enquiry now gets this reply." });
			})
			.catch(e => this.setState({ busy: false, error: errorText(e, "That didn't save. Please try again.") }));
	}

	reset() {
		const f = this.form();
		this.setState({ busy: true, error: null, notice: null });
		formReplyDELETE(f.key)
			.then(r => {
				this.replaceForm(r.data);
				this.setState(
					{ busy: false, confirmReset: false, draft: this.current(r.data), notice: f.label + " is back on the built-in reply." },
					() => this.showSending(r.data)
				);
			})
			.catch(e => this.setState({ busy: false, error: errorText(e, "That didn't reset. Please try again.") }));
	}

	discard() {
		const f = this.form();
		this.setState({ draft: this.current(f), notice: null, error: null, confirmReset: false }, () => this.showSending(f));
	}

	renderList() {
		const { forms, key } = this.state;
		return (
			<div className="fr-list">
				{forms.map(f => (
					<button type="button" key={f.key} className={"fr-item" + (f.key === key ? " on" : "")} onClick={() => this.select(f.key)}>
						<span className="fr-item-label">{f.label}</span>
						<span className={"fr-badge" + (f.saved ? " edited" : "")}>{f.saved ? "edited" : "built-in"}</span>
					</button>
				))}
			</div>
		);
	}

	renderEditor() {
		const f = this.form();
		const { draft, busy, confirmReset, previewing } = this.state;
		const dirty = this.dirty();
		return (
			<div>
				<h4 className="fr-title">{f.label}</h4>
				<p className="fr-muted">Filled in on: {f.where}</p>
				<p className="fr-sending">
					{f.saved ? (
						<span>
							Sending the reply saved by <b>{f.updated_by || "an admin"}</b>
							{f.updated_at ? " on " + DateFns.format(parseAPIDate(f.updated_at), "ddd D MMM YYYY, h:mm A") : ""}.
						</span>
					) : (
						<span>
							Sending the built-in {f.applicant ? "applicant" : "customer"} reply. Edit below and save to give this form its own.
						</span>
					)}
				</p>

				<div className="form-group">
					<label>Subject line</label>
					<div className="fr-fixed">{f.subject}</div>
					<p className="fr-hint">Fixed: the subject line is not part of the reply you write.</p>
				</div>

				<div className="form-group">
					<label htmlFor="fr-preview">Inbox preview line</label>
					<input id="fr-preview" className="form-control" maxLength={150} value={draft.preview} onChange={e => this.set("preview", e.target.value)} />
					<p className="fr-hint">Shown beside the subject in most inboxes; hidden in the message itself.</p>
				</div>

				<div className="form-group">
					<label htmlFor="fr-heading">Heading</label>
					<input id="fr-heading" className="form-control" maxLength={150} value={draft.heading} onChange={e => this.set("heading", e.target.value)} />
				</div>

				<div className="form-group">
					<label htmlFor="fr-body">Message</label>
					<textarea id="fr-body" className="form-control fr-text" rows={10} maxLength={4000} value={draft.body} onChange={e => this.set("body", e.target.value)} />
					<p className="fr-hint">
						Leave a blank line between paragraphs. Put ** either side of words to make them bold, like **today**. 0435-SKIMPY, 0435 754 679,
						skimpyheaven.com.au and @skimpyheaven.com.au addresses become links by themselves.
					</p>
					<p className="fr-hint">
						To make words a link, put them in square brackets with the web address straight after in round brackets, like [fill in the
						information form](https://…). A full web address starting with https:// becomes a link by itself. Links must start with https://.
					</p>
				</div>

				<div className="checkbox">
					<label>
						<input type="checkbox" checked={draft.call_button} onChange={e => this.set("call_button", e.target.checked)} /> Show the 0435-SKIMPY call
						button under the message
					</label>
				</div>
				<div className="checkbox">
					<label>
						<input type="checkbox" checked={draft.centred} onChange={e => this.set("centred", e.target.checked)} /> Centre the text
					</label>
				</div>

				<div className="form-group">
					<label htmlFor="fr-closing">Under the line</label>
					<textarea id="fr-closing" className="form-control fr-text" rows={3} maxLength={2000} value={draft.closing} onChange={e => this.set("closing", e.target.value)} />
					<p className="fr-hint">Optional. A dividing line separates it from the message. Bold and links work here too.</p>
				</div>

				<div className="fr-actions">
					<button type="button" className="btn btn-primary" disabled={busy || !dirty || !draft.body.trim()} onClick={() => this.save()}>
						Save reply
					</button>
					{dirty && (
						<button type="button" className="btn btn-default" disabled={busy} onClick={() => this.discard()}>
							Discard changes
						</button>
					)}
					{f.saved &&
						!confirmReset && (
							<button type="button" className="btn btn-link" disabled={busy} onClick={() => this.setState({ confirmReset: true })}>
								Reset to built-in
							</button>
						)}
					{confirmReset && (
						<span className="fr-confirm">
							This deletes the saved wording and goes back to the built-in reply.{" "}
							<button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => this.reset()}>
								Reset
							</button>{" "}
							<button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => this.setState({ confirmReset: false })}>
								Keep it
							</button>
						</span>
					)}
				</div>

				<h5 className="fr-preview-title">
					Preview {dirty ? "(not saved yet)" : ""} {previewing && <span className="fr-muted">updating…</span>}
				</h5>
				<iframe title="Email preview" className="fr-frame" sandbox="" srcDoc={this.state.previewHTML} />
			</div>
		);
	}

	render() {
		const { forms, draft, error, notice } = this.state;
		return (
			<div>
				<h4 className="heading">Website form replies</h4>
				<MiniContainer>
					<p className="fr-intro">
						What each form on skimpyheaven.com.au emails back to the person who filled it in. Every form sends its built-in reply until you save one
						for it here.
					</p>
					{error && <div className="alert alert-danger">{error}</div>}
					{notice && <div className="alert alert-success">{notice}</div>}
					{!forms && !error && <Spinner loading={true} />}
					{forms && draft && (
						<div className="row">
							<div className="col-md-3">{this.renderList()}</div>
							<div className="col-md-9">{this.renderEditor()}</div>
						</div>
					)}
				</MiniContainer>
			</div>
		);
	}
}

export default FormReplies;
